/**
 * 회의록 «보내기» 와 «보낸 이력» — 2026-09-22.
 *
 * ## ⚠⚠ 서버가 아직 이것을 «안 낸다» — 그것은 «오류»가 아니라 «상태»다
 *
 * 보내는 라우트와 이력은 백엔드가 짓고 있다. 그때까지 **404** 가 오는데, 404 를 빨간 오류로 그리면
 * 쓰는 사람이 **고장으로 읽는다.** 그래서 이 모듈이 404 를 `{ ready: false }` 로 **갈라서** 돌려준다 —
 * 라우트가 서는 순간 화면이 **저절로** 열린다(`admin/frontend/src/api/voiceStorage.ts` 와 같은 수법).
 *
 * ⛔ 500·502·401 은 **그대로 던진다** — 뭉치면 진짜 고장이 숨는다(코드를 알아본 502 는 예외다).
 * ★ 읽기·쓰기 **둘 다** 404 를 `code` 칸 유무로 가른다 — 라우트 미등록과 「없는·못 보는 회의록」은
 *   뜻이 다르다. 쓰기는 거기에 503 둘·413·메일 쪽 거절까지 더해 **아홉 갈래**다
 *   (아래 `SendMinutesOutcome`).
 *
 * ## ★★★ 읽기와 쓰기가 «서로 다른 서비스»다 (2026-09-22 확정)
 *
 * ```
 * 읽기(이력)  GET  /minutes/api/v1/minutes/:id/sends   minutes-svc · minutesClient
 *             까닭: 이력은 회의록의 소유물이고 새 표(`meeting_minute_sends`)가 거기 선다.
 * 쓰기(보내기) POST /api/v1/minutes/:id/sends           chat-server · apiClient
 *             까닭: minutes-svc 엔 **MinIO 도 SMTP 도 서명 키도 없다**
 *             (`minutes-attachments.routes.ts:6`). 실제로 보내는 일은 chat-server 몫이고,
 *             chat-server 가 보낸 뒤 `POST /internal/minutes/sends` 로 이력 한 줄을 적는다.
 * ```
 * ⛔ 이 둘을 한 client 로 묶지 않는다 — 묶으면 한쪽이 404 이고 다른 쪽이 200 인 상태를 못 읽는다.
 *
 * ## ★ 계약이 «다» 섰다 (2026-09-22 · PR #1608·#1616)
 *
 * 읽기(이력)·쓰기(보내기·메일 갈래까지) 라우트가 모두 main 에 있다. 그래서 이 파일의 타입·코드
 * 글자는 **가정이 아니라 그 파일들을 열어 맞춘 것**이다(어디를 봤는지는 각 상수·타입 주석에 적었다).
 *
 * ## ⚠⚠ 계약이 서기 «전»의 가정은 서고 나면 반드시 대조한다 — 셋이 틀렸다
 *
 * ```
 * ① 받는 사람에 `user_id` 칸       → 없었다. 실제는 kind:'user'|'email'|'room' + id/email/name
 * ② 201 의 `recipients` 가 명단    → «수»였다(recipients.length)
 * ③ 메일 본문 상한 1,000,000자     → 그 수에는 «닿을 수 없다»(Fastify 1MB ≈ 한글 349,000자 → 413)
 * ```
 * ★ 셋 다 **이름만 보고 뜻을 넘겨짚어** 생긴 것이다(「recipients」라는 이름 · 스키마에 적힌 수).
 *   그래서 이제 **전언을 옮길 때마다 그 줄을 열어 본다** — 그 규율이 셋을 잡았다.
 *
 * 타입이 `packages/shared-types` 로 오면 그쪽을 import 하고 여기 것을 지운다(8443 은 이미
 * `@workchat/shared-types` 를 쓴다 — `api/client.ts` 선례).
 */
import axios from 'axios';
import { apiClient } from './client';
// ★ 레포에 이미 선 관용구를 쓴다(약 10곳에서 쓴다) — 같은 뜻을 다른 글자로 두 벌 만들지 않는다.
import { extractErrorCode, extractErrorMessage } from './approval';
import { minutesClient } from './minutes';
import type { MinutesSendChannel } from '../pages/AiMinutesPage/minutes-send-core';

/**
 * 보낸 한 건.
 *
 * ## ⛔ 「닿았다」는 칸이 «없다»
 *
 * 이력은 **「보냈다」를 적는 것**이고 「닿았다」가 아니다(사용자 결정). 메일이 반송돼도 이력에는
 * 보낸 것으로 남는다 — 그 구별은 **보낸메일함**이 한다. 그래서 `delivered`·`status` 같은 칸을
 * 두지 않고, 화면에도 「전달됨」 같은 말을 쓰지 않는다. 칸이 있으면 언젠가 그 말이 붙는다.
 */
export interface MinutesSendRecord {
  id: string;
  minutes_id: string;
  channel: MinutesSendChannel;
  /** 보낸 사람(user id 문자열). */
  sent_by: string;
  /** 보낸 시각(ISO). 「닿은」 시각이 아니다. */
  sent_at: string;
  /**
   * 받는 사람 — ★ **이름·주소가 그대로 남는다.**
   * 되돌리기가 없으니 이력이 유일한 되짚을 길이다. 그래서 서버는 수를 세 주지 않고 **명단**을 준다
   * (`recipients: z.array(recipient).min(1)` — 빈 명단은 서버가 거절한다).
   */
  recipients: MinutesSendRecipient[];
  /** 그 채널 쪽 문서의 손잡이 — 방 id · 메일 id · 결재 문서 id 같은 것. 없으면 null. */
  channel_ref: string | null;
  /** 보낸 제목. 없으면 null. */
  subject: string | null;
}

/**
 * 받는 사람 하나 — ⛔ **서버 계약 그대로다**(`apps/minutes-svc/src/repo/sends.repo.ts:28-36`).
 *
 * ## ⚠ 2026-09-22 정정 — 처음 판의 `user_id` 라는 칸은 «없었다»
 *
 * 계약이 없던 때 `{ user_id?, name?, email? }` 로 가정했는데, 실제로 선 서버(PR #1608)는
 * **`kind` 로 갈리는 세 모양**을 준다. 옛 가정으로도 이름·주소는 우연히 그려졌지만,
 * **`kind:'room'` 줄은 이름도 주소도 없어** 화면이 「받는 사람 기록이 없습니다」로 그렸다 —
 * 실제로는 «대화방으로 보낸 것»이다.
 *
 * ⛔ `kind` 가 셋인 까닭(서버 주석): **사내 사람은 id 로, 외부는 주소로** 남아야 한다. 한 모양으로
 *   뭉치면 사내 사람이 주소로 적히고, 그 사람이 주소를 바꾸면 **되짚을 실이 끊긴다.**
 */
export interface MinutesSendRecipient {
  kind: 'user' | 'email' | 'room';
  /** `user` 면 사용자 id · `room` 이면 방 id. `email` 이면 없다. */
  id?: string;
  /** `email` 이면 주소. ⛔ 마스킹하지 않는다 — 「외부 주소 아무나」가 결정이고, 가린 주소는 못 되짚는다. */
  email?: string;
  /** 그때의 표시 이름 — ★ 조직도에서 온다. ⛔ 전사에서 오지 않는다(전사는 이름을 틀린다). */
  name?: string;
}

/** 보낼 때 실어 보내는 것. ⛔ 녹음·첨부를 «고르는 칸이 없다» — 회의록 본문만 간다(사용자 결정). */
export interface SendMinutesPayload {
  /** ⛔ `approval` 이 없다 — 결재는 **기안 화면**으로 넘긴다(결재선을 거기서 정해야 한다). */
  channel: Exclude<MinutesSendChannel, 'approval'>;
  /** 사내 받는 사람(user id). 팀·부서째 고른 것은 화면이 사람으로 펼쳐서 준다. */
  user_ids: string[];
  /** 외부 메일 주소. `channel:'mail'` 에서만 쓴다. */
  emails: string[];
  /**
   * ★★ 보낼 글자 — **화면이 만들어 싣는다.**
   * 서버가 다시 만들면 **미리보기로 본 것과 실제로 간 것이 달라질 수 있다**(되돌리기가 없으니
   * 그 어긋남은 되짚을 수 없다). 만드는 규칙은 `minutes-send-core.ts` 의 `buildSendText` 한 곳뿐이다.
   */
  subject: string;
  body: string;
}

export type MinutesSendHistoryResult =
  | { ready: true; items: MinutesSendRecord[] }
  /** 라우트가 아직 없다(배포 안 됨) — 고장이 아니다. `code` 칸 «없는» 404. */
  | { ready: false }
  /**
   * 없는·못 보는 회의록 — `404 + code:'NOT_FOUND'`.
   *
   * ★ 2026-09-22 에 가를 수 있게 됐다: minutes-svc 404 에 **`code` 가 붙었다**(실측: `code:'NOT_FOUND'`
   *   가 `minutes.routes.ts`·`minutes-sends.routes.ts`·`minutes-attachments.routes.ts` 세 파일에 있다).
   *   그전에는 Fastify 기본 404 와 둘 다 `code` 가 없어 **`error` 의 대소문자 차이**에 기대야 했고,
   *   그래서 가르지 않고 한계로 적어 두었다. 이제 보내기와 **같은 방식**으로 가른다.
   */
  | { ready: 'not-found' };

/**
 * 보낸 뒤 서버가 돌려주는 것 — ⛔ **이력의 한 줄과 «다른 모양»이다.**
 *
 * ## ★★★ 이것을 이력 목록에 그대로 꽂으면 화면이 어긋난다
 *
 * ```
 * 201 응답    { channel, room_id, message_id, recipients, truncated, logged }
 * 이력 한 줄  { id, minutes_id, channel, sent_by, sent_at, recipients, channel_ref, subject }
 * ```
 * `sent_at` 도 `id` 도 없다. 처음 판은 이 객체를 `sends.add()` 로 이력 맨 위에 꽂았는데, 그러면
 * **방금 보낸 줄이 시각 없이·key 없이** 그려진다. 그래서 «가짜 줄»을 만들지 않고 **이력을 다시 읽는다.**
 */
export interface MinutesSendAck {
  channel: Exclude<MinutesSendChannel, 'approval'>;
  /** 메시지 갈래에서 새로 만든 단체방. */
  room_id?: string | null;
  message_id?: string | null;
  /** 메일 갈래에서 만들어진 메일 문서 id. */
  mail_id?: string | null;
  /**
   * ★★ **수다 — 명단이 «아니다».**
   *
   * 실측(2026-09-22, 라우트가 main 에 든 뒤): 두 갈래 모두 `recipients: recipients.length` 를 보낸다
   * (`apps/chat-server/src/modules/minutes/minutes-send.routes.ts` 의 201 두 곳).
   * ⚠ 처음에 `MinutesSendRecipient[]` 로 적어 뒀다 — 전언을 옮길 때 「recipients」라는 «이름»만 보고
   *   이력의 그 칸과 같은 것으로 읽었다. 화면이 안 쓰던 칸이라 아무 일도 안 났지만 **타입이 거짓**이었고,
   *   다음 사람이 `ack.recipients.map(...)` 을 쓰면 그때 터진다.
   * ★ 명단이 필요하면 **이력을 다시 읽는다** — 그것이 이 판의 규칙이다(아래 `logged` 주석).
   */
  recipients?: number;
  /** 서버도 본문을 잘랐나 — 화면이 보내기 «전»에 이미 알렸지만, 보낸 «뒤»에도 사실을 적는다. */
  truncated?: boolean;
  /**
   * ★★★ **이력을 적었나.** `false` 면 「보냈는데 **되짚을 줄이 없다**」는 뜻이다.
   *
   * 되돌리기가 없어 이력이 유일한 되짚을 길인데 그 줄이 없을 수 있다. 201 만 보고 「보냈습니다」로
   * 끝내면 사람은 **이력에 안 남은 것을 모른다.**
   * ⚠ 없으면(`undefined`) «모른다»는 뜻이다 — ⛔ `true` 로 접지 않는다(안 남았는데 남았다고 보인다).
   *   그래서 화면은 `=== false` 일 때만 말한다.
   */
  logged?: boolean;
}

/**
 * 보내기의 끝 — **아홉 가지다.** ⛔ 뜻이 다른 것을 하나로 뭉치지 않는다.
 *
 * ```
 * sent                보냈다(ack 안에 truncated·logged 가 있다)
 * route-missing       라우트가 «아직 없다» — Fastify 기본 404(code 칸 «없음»)
 * not-found           404 + code:'NOT_FOUND' — 없는 회의록 «또는» 못 보는 회의록
 * channel-not-ready   503 + code:'CHANNEL_NOT_READY' — 그 «갈래»만 아직 안 선다(옛 배포에서만)
 * minutes-unavailable 503 + code:'MINUTES_UNAVAILABLE' — 지금 «물어볼 수 없다»(나중엔 된다)
 * gateway             본문이 JSON 이 아니다 — 앞단(프록시·엣지)이 답했다
 * needs-setup         SMTP 미등록·사용불가 — ★ 사용자가 «고칠 수 있다»
 * send-failed         502 SEND_FAILED — ★ 사용자가 «못 고친다»(받는 메일 서버가 안 받았다)
 * body-too-large      413 — ⚠ Fastify 가 핸들러 «전»에 낸다. 받는 사람 문제가 «아니다»
 * refused             그 밖의 4xx — 서버가 «다시 확인»해서 거절했다. 사유를 화면이 읽는다
 * ```
 *
 * ## ⛔⛔ status 로 가르면 «안 된다» — 503 이 둘이다
 *
 * `CHANNEL_NOT_READY` 와 `MINUTES_UNAVAILABLE` 이 **같은 503** 인데 뜻이 다르다(하나는 「그 길이
 * 아직 없다」, 하나는 「지금 물어볼 수 없다」). 그래서 **코드 문자열**로 가른다.
 *
 * ## ⛔⛔ 404 도 둘이다 — «code 칸 유무»로 가른다
 *
 * 라우트가 없으면 Fastify 기본 404 가 오는데 그 본문은
 * `{ message, error:'Not Found', statusCode:404 }` 로 **`code` 칸이 없다**
 * (실측: `fastify@4.28.1/lib/fourOhFour.js:49-60` · chat-server 에 `setNotFoundHandler` 0건).
 * 라우트가 서면 「없는·못 보는 회의록」이 `code:'NOT_FOUND'` 로 온다.
 * ⛔ 이 둘을 뭉치면 **「남의 회의록」이 「서버가 아직 준비 중」으로 보인다** — 틀린 안내다.
 *
 * ## ★ 코드는 «`code` 칸만» 본다 (2026-09-22 좁혔다)
 *
 * 처음 판은 `error` 칸도 코드로 읽었다. 실제 라우트의 거절이 **전부 `code`** 로 확인됐고,
 * 무엇보다 **Fastify 기본 404 의 `error:'Not Found'` 를 «코드»로 읽어 버리는 것**이 해롭다
 * (404 를 코드로 가르는 판정이 오판한다). 그래서 좁혔다 — 그 사실을 시험이 잠근다.
 */
/*
 * ⚠⚠ **아홉 갈래 중 «운영에서 실제로 본 것»은 하나다** (2026-09-22 운영 확인 뒤 기록).
 *
 * ```
 * 실제로 본 것   sent — 메시지 201(recipients 1 · room_id 새로 생성 · logged true)
 *                     메일 201(mail_id 1447 · 사용자 육안으로 수신함 확인)
 * ⛔ 못 본 것    오류 «코드 글자»가 하나도 안 나왔다 — 정상 경로만 탔다:
 *               route-missing · not-found · channel-not-ready · minutes-unavailable
 *               gateway · needs-setup(NO_SENDING_ACCOUNT) · send-failed
 *               body-too-large(413) · refused · truncated:true · logged:true 아닌 것
 * ```
 * ⇒ 위 코드 문자열들을 잠근 시험은 **여전히 «가정»이다.** 서버 소스·Fastify 소스에서 읽은 글자이고
 *   **주고받은 응답으로 확인한 것이 아니다.** ⛔ 「실측했다」고 옮겨 적지 말 것.
 * ★ 그 갈래 하나라도 운영에서 실제로 뜨면, 그때 본 응답 본문을 이 주석에 적어 가정을 지운다.
 */
export type SendMinutesOutcome =
  | { kind: 'sent'; ack: MinutesSendAck }
  | { kind: 'route-missing' }
  | { kind: 'not-found' }
  | { kind: 'channel-not-ready' }
  | { kind: 'minutes-unavailable' }
  | { kind: 'gateway' }
  /**
   * ★ **사용자가 «고칠 수 있는» 것** — 보내는 메일 계정(SMTP)이 없거나 못 쓴다.
   *   ⛔ `send-failed` 와 같은 말로 덮지 않는다: 이쪽은 «가서 등록하면» 되고 저쪽은 «기다리는» 수밖에 없다.
   *   서버 메시지가 이미 갈 곳을 말해 준다(「개인설정 > 외부 메일 계정에 …」) — 그래서 그 글자를 싣는다.
   */
  | { kind: 'needs-setup'; message: string }
  /** ★ **우리 쪽 잘못이 아닌** 것 — 받는 메일 서버가 안 받았다(502 `SEND_FAILED`). */
  | { kind: 'send-failed'; message: string }
  /**
   * ★ 본문이 너무 길다 — ⚠ **나머지와 성격이 다르다.** 우리 라우트가 아니라 **Fastify 가 핸들러
   *   «전»에** 낸다. 그래서 「받는 사람·주소를 고치라」는 뜻이 **전혀** 아니다.
   */
  | { kind: 'body-too-large' }
  | { kind: 'refused'; message: string };

/**
 * 본문이 «JSON 객체»인가.
 *
 * ## ⚠⚠ 404 가, 심지어 **200 이** JSON 이 아닐 수 있다
 *
 * `code` 칸으로 가르려면 본문이 객체여야 하는데, 그 응답이 **chat-server 에서 오지 않을 수 있다**:
 *
 * ```
 * · 로컬 dev 는 vite 프록시를 거친다(/api → :13000). 프록시가 못 넘기면 vite 가 **index.html** 을
 *   준다 → **status 200 · 본문 HTML**. 「404 도 아닌데 JSON 도 아닌」 경우다.
 * · 운영은 엣지(axi-edge)를 거친다 — 엣지가 내는 404·502 는 **HTML** 일 수 있다.
 * ```
 *
 * ⛔ 그래서 이것을 「라우트가 없다」와 **같은 문구로 덮지 않는다.** HTML 이 온 것은 «라우트가 없다»가
 *   아니라 «앞단에서 막혔다»이고, 덮어 버리면 **다음 사람이 배관을 의심하지 않는다.**
 *   `gateway` 갈래가 그것을 위해 있다.
 */
function isJsonObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * ⛔ 서버와 **같은 글자**여야 한다. 전부 `code` 칸으로 온다.
 *
 * ★ 아래 줄 번호는 **내가 origin/main 을 열어 직접 확인한 것**이다(전언을 옮긴 것이 아니다):
 * ```
 * NOT_FOUND                 apps/chat-server/src/modules/minutes/minutes-send.routes.ts:134   404
 * MINUTES_UNAVAILABLE       apps/chat-server/src/modules/minutes/minutes-send.routes.ts:143   503
 * NO_SENDING_ACCOUNT        apps/chat-server/src/modules/mail/mail.routes.ts:468              400  ★ 고칠 수 있다
 * SENDING_ACCOUNT_UNUSABLE  apps/chat-server/src/modules/mail/mail.routes.ts:464              400  ★ 고칠 수 있다
 * SEND_FAILED               apps/chat-server/src/modules/mail/mail.routes.ts:489              502  ★ 못 고친다
 * ```
 * ⚠ `INVALID_EXTERNAL_ADDRESS`·`INVALID_RECIPIENTS`·`NO_RECIPIENTS`·`BAD_REQUEST`·
 *   `TOO_MANY_RECIPIENTS`·`MAIL_REJECTED` 는 **따로 안 받는다** — 전부 `refused` 로 가고
 *   **서버 메시지를 그대로** 보여 준다. 그 메시지가 고칠 것을 이미 말한다(실측으로 확인했다).
 */
export const MINUTES_UNAVAILABLE_CODE = 'MINUTES_UNAVAILABLE';
export const NOT_FOUND_CODE = 'NOT_FOUND';
export const SEND_FAILED_CODE = 'SEND_FAILED';
/** SMTP 가 없거나 못 쓴다 — 둘 다 「가서 등록하면 되는」 것이라 한 갈래로 받는다. */
export const NEEDS_SETUP_CODES: readonly string[] = ['NO_SENDING_ACCOUNT', 'SENDING_ACCOUNT_UNUSABLE'];
/**
 * 본문이 한도를 넘겼을 때 **Fastify 가** 내는 코드(413).
 *
 * ⚠ 이 코드는 **내 실측이 아니다** — 관제 전언이다. 뒷받침으로 확인한 것은 「chat-server 가
 *   보내기·메일 라우트에 `bodyLimit` 을 안 걸어 Fastify 기본 1MB 가 적용된다」는 사실뿐이다.
 * ★ 그래서 코드 글자에만 기대지 않고 **status 413 도 함께** 본다 — 글자가 달라도 잡힌다.
 */
export const BODY_TOO_LARGE_CODE = 'FST_ERR_CTP_BODY_TOO_LARGE';

/**
 * ⚠ **메일 갈래는 이제 실제로 보낸다**(PR #1616). 그래서 서버는 이 코드를 **더는 안 준다** —
 *   실측: `minutes-send.routes.ts` 에 `CHANNEL_NOT_READY` 가 **0건**이다.
 *
 * ★ 그런데 **지우지 않는다.** 아직 그 판이 배포되지 않은 서버는 여전히 이 코드를 주고, 그때
 *   「준비 중」이라고 말해 주는 것이 맞다. 배포가 다 돌면 그때 걷어낸다.
 */
export const CHANNEL_NOT_READY_CODE = 'CHANNEL_NOT_READY';

/**
 * 받는 사람 상한 — 서버가 넘으면 `TOO_MANY_RECIPIENTS`(400)로 거절한다.
 *
 * ★ 화면이 **보내기 전에** 센다 — 되돌릴 수 없는 일이라 400 을 맞고 나서 아는 것보다 낫다.
 * ⚠ 근거 둘: 관제 전언(「200명 초과」) + 이력 스키마의 `.max(200)`(`minutes-sends.routes.ts:60`).
 *   **보내는 라우트로 직접 재지는 못했다**(그 라우트가 아직 main 에 없다).
 */
export const MAX_SEND_RECIPIENTS = 200;

/**
 * 본문의 «코드»를 꺼낸다 — ⛔ **`code` 칸만** 본다.
 *
 * `error` 칸을 함께 보면 Fastify 기본 404 의 `error:'Not Found'` 가 코드로 읽힌다(위 docblock).
 */
export function serverCodeOf(err: unknown): string | null {
  // ★ 판정은 **한 곳**이다 — `api/approval.ts` 의 `extractErrorCode`. 같은 일을 두 글자로 두면
  //   어느 날 한쪽만 고쳐져 「메일은 가르는데 결재는 못 가르는」 상태가 된다.
  return extractErrorCode(err);
}

/** ⛔ 404 «만». 500·502·401 은 진짜 탈이라 오류로 보여야 한다. */
function isNotServedYet(e: unknown): boolean {
  return axios.isAxiosError(e) && e.response?.status === 404;
}

/**
 * 보낸 이력. 회의록 상세를 열 때 함께 부른다.
 *
 * ⚠ 이력이 비어 있는 것과 라우트가 없는 것은 **다른 일**이다 — 전자는 「아직 안 보냈다」,
 *   후자는 「서버가 아직 이것을 줄 줄 모른다」. 화면 문구도 둘이 다르다.
 *
 * ## ★ 이 404 는 «두 가지»이고, 이제 «가른다» (2026-09-22)
 *
 * ```
 * 배포가 아직 안 된 서버   Fastify 기본 404   `code` 칸 «없음»      → ready:false
 * 없는·못 보는 회의록      라우트의 404       `code:'NOT_FOUND'`   → ready:'not-found'
 * ```
 * ⚠ 한때는 가르지 못했다 — minutes-svc 가 `error` 칸만 써서 둘 다 `code` 가 없었고, 가르려면
 *   `error` 의 대소문자 차이(`'NOT_FOUND'` vs `'Not Found'`)에 기대야 했다. 그래서 한계로 적어
 *   두고 서버에 넘겼고, **서버가 404 에 `code` 를 붙여 풀렸다**(실측: 세 라우트 파일에 `code:'NOT_FOUND'`).
 *
 * ★ 실제로 닿기는 여전히 드물다 — 이력을 부르는 자리는 «상세를 이미 연 화면»이고 상세도 이력과
 *   **같은 잣대**(`getOwnedById`)를 쓰므로, `not-found` 는 **그 사이 지워진 경쟁**일 때 온다.
 *   드물다고 뭉치지는 않는다: 뭉치면 그 드문 한 번에 **틀린 안내**가 나간다.
 */
export async function listMinutesSends(minutesId: string): Promise<MinutesSendHistoryResult> {
  try {
    const r = await minutesClient.get<{ items?: MinutesSendRecord[] }>(`/minutes/${minutesId}/sends`);
    // 앞단(프록시·엣지)이 index.html 을 주면 객체가 아니다 — 빈 이력으로 읽지 않는다.
    if (!isJsonObject(r.data)) throw new Error('이력 응답이 JSON 이 아닙니다');
    return { ready: true, items: Array.isArray(r.data.items) ? r.data.items : [] };
  } catch (e: unknown) {
    if (isNotServedYet(e)) {
      /*
       * ⛔ 404 가 둘이다 — 보내기와 **같은 방식**으로 `code` 칸 유무로 가른다.
       *   뭉치면 「없는·못 보는 회의록」에도 「서버가 아직 이것을 줄 줄 모릅니다」가 뜬다.
       */
      return serverCodeOf(e) === null ? { ready: false } : { ready: 'not-found' };
    }
    throw e;
  }
}

/**
 * 첨부가 «몇 개» 붙어 있나 — 보내기 화면에서 「함께 가지 않습니다」를 적기 위해서다.
 *
 * ## ⚠ 이 숫자가 없으면 사람이 «갔다고» 오해한다
 *
 * 회의록엔 사진·녹음 첨부가 붙고 **작성자만** 본다(`minutes-attachments.routes.ts:15-21`).
 * 보내는 것은 본문뿐이라 첨부는 안 간다 — 그런데 **아무 말도 없으면 함께 간 줄로 읽는다.**
 * ⛔ 「함께 보낼까요」라는 **칸을 두지 않는다**(끌 수 있게 두면 언젠가 켜진다) — 사실만 적는다.
 *
 * 실패하면 0 으로 접는다. 이 숫자 하나 때문에 보내기 화면이 안 열리면 안 된다.
 */
export async function countMinutesAttachments(minutesId: string): Promise<number> {
  try {
    const r = await minutesClient.get<{ items?: unknown[] }>(`/minutes/${minutesId}/attachments`);
    return Array.isArray(r.data?.items) ? r.data.items.length : 0;
  } catch {
    return 0;
  }
}

/**
 * 보낸다. ⛔ **되돌릴 수 없다.**
 *
 * ## ⚠ 여기서 404 를 「아직 안 섰다」로 읽는 것은 «한 번만» 안전하다
 *
 * 목록(GET)이 `ready:true` 를 줬다면 라우트는 **이미 서 있다**. 그 뒤에 온 404 는 「없는 회의록」
 * 이거나 「남의 회의록」(minutes-svc 는 남의 것과 없는 것을 **같은 404** 로 준다 — `ownedOr404`)이다.
 * 그래서 이 함수의 `ready:false` 는 **누르기 전에** 화면이 이미 알고 있는 상태를 확인하는 용도이고,
 * 화면은 목록이 `ready:false` 인 동안 **보내기 단추를 잠근다**(누를 일이 없게 한다).
 */
export async function sendMinutes(minutesId: string, payload: SendMinutesPayload): Promise<SendMinutesOutcome> {
  try {
    // ★ chat-server 다(apiClient) — minutes-svc 가 아니다. 위 「읽기와 쓰기가 서로 다른 서비스」 참조.
    const r = await apiClient.post<unknown>(`/minutes/${minutesId}/sends`, payload);
    /*
     * ⚠ **200 인데 HTML** 일 수 있다 — vite 프록시가 못 넘기면 `index.html` 이 온다.
     *   그것을 「서버가 기록을 안 줬다」로 읽으면 **앞단 고장이 서버 탓으로 보인다.**
     */
    if (!isJsonObject(r.data)) return { kind: 'gateway' };
    const ack = (r.data as { send?: MinutesSendAck }).send;
    if (!ack) {
      // 201 이고 JSON 인데 send 가 없다 — 계약 위반이다(앞단 문제가 아니라 서버 문제).
      throw new Error('서버가 보낸 기록을 돌려주지 않았습니다');
    }
    return { kind: 'sent', ack };
  } catch (e: unknown) {
    if (!axios.isAxiosError(e)) throw e;
    /*
     * 응답이 «아예 없다» — 네트워크 단절·시간 초과다. 앞단이 답한 것과 다르니 그대로 던져
     * 「보내지 못했습니다: …」로 보이게 한다(전역 처리가 볼 몫도 있다).
     */
    if (e.response === undefined) throw e;
    /* 본문이 JSON 이 아니면 앞단(프록시·엣지)이 답한 것이다 — 라우트 없음과 «가른다». */
    if (!isJsonObject(e.response.data)) return { kind: 'gateway' };

    const status = e.response.status;
    const code = serverCodeOf(e);

    /*
     * ⛔ 404 가 둘이다 — «code 칸 유무»로 가른다(위 docblock 의 실측 근거).
     *   뭉치면 「남의 회의록」이 「서버가 아직 준비 중」으로 보인다.
     */
    if (status === 404) return code === null ? { kind: 'route-missing' } : { kind: 'not-found' };

    /*
     * ⛔ **413 을 «맨 앞»에 둔다.** 이것은 우리 라우트가 아니라 **Fastify 가 핸들러 «전»에** 낸다 —
     *   다른 400 들과 같은 문구를 쓰면 사용자가 **받는 사람·주소를 고치려 든다.**
     * ★ 코드 글자와 status 를 **둘 다** 본다: 글자는 내 실측이 아니라 전언이므로, 글자가 달라도
     *   413 이면 잡힌다(반대로 413 이 아닌데 그 글자가 올 일은 없다).
     */
    if (status === 413 || code === BODY_TOO_LARGE_CODE) return { kind: 'body-too-large' };

    /* ⛔ 503 도 둘이다 — status 가 같으니 반드시 코드로 가른다. */
    if (code === CHANNEL_NOT_READY_CODE) return { kind: 'channel-not-ready' };
    if (code === MINUTES_UNAVAILABLE_CODE) return { kind: 'minutes-unavailable' };

    /*
     * ★ 메일 갈래가 저쪽 거절을 «그대로 흘려보낸다»(실측: `minutes-send.routes.ts` 가 code·message 를
     *   옮기고, code 없는 답만 `MAIL_REJECTED` 로 감싼다). 그 가운데 둘은 갈라야 한다:
     *   · SMTP 미등록·사용불가 → **가서 등록하면 된다**
     *   · SEND_FAILED(502)    → **우리 쪽에서 할 것이 없다** (그리고 5xx 라 아래 4xx 분기에 안 걸린다)
     */
    if (code !== null && NEEDS_SETUP_CODES.includes(code)) {
      return { kind: 'needs-setup', message: extractErrorMessage(e) };
    }
    if (code === SEND_FAILED_CODE) return { kind: 'send-failed', message: extractErrorMessage(e) };

    /*
     * ★ 서버가 «다시 확인»한다 — 화면이 보낸 user_ids·emails 를 서버가 그대로 믿지 않는다
     *   (백엔드가 그은 경계: 본문은 화면이 만들고, 「보낼 수 있나」는 서버가 정한다).
     *   그래서 거절 사유를 **서버 본문에서** 읽어 그대로 보여 준다 — axios 의
     *   「Request failed with status code 400」을 보여 주면 사람이 무엇을 고쳐야 할지 모른다.
     * ⛔ 4xx(401·403 뺀)만 거절로 보고 5xx 는 던진다 — 코드를 못 알아본 503 은 엣지·게이트웨이가
     *   낸 «진짜 고장»일 수 있어 상태로 접으면 숨는다.
     */
    if (status >= 400 && status < 500 && status !== 401 && status !== 403) {
      return { kind: 'refused', message: extractErrorMessage(e) };
    }
    throw e;
  }
}
